import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, RotateCw, RotateCcw, Maximize2 } from "lucide-react";

const MIN = 1, MAX = 6;
const clamp = (v) => Math.min(MAX, Math.max(MIN, v));
const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

export const ZoomableImage = ({ src, alt = "Receipt", className = "" }) => {
  const [scale, setScale] = useState(1);
  const [rot, setRot] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const gesture = useRef(null);

  const reset = () => { setScale(1); setRot(0); setPos({ x: 0, y: 0 }); };
  const zoom = (next) => { const s = clamp(next); setScale(s); if (s === 1) setPos({ x: 0, y: 0 }); };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) gesture.current = { kind: "pinch", d: dist(e.touches), s: scale };
    else if (e.touches.length === 1 && scale > 1) gesture.current = { kind: "pan", x: e.touches[0].clientX - pos.x, y: e.touches[0].clientY - pos.y };
  };
  const onTouchMove = (e) => {
    const g = gesture.current;
    if (!g) return;
    e.preventDefault();
    if (g.kind === "pinch" && e.touches.length === 2) zoom(g.s * (dist(e.touches) / g.d));
    else if (g.kind === "pan" && e.touches.length === 1) setPos({ x: e.touches[0].clientX - g.x, y: e.touches[0].clientY - g.y });
  };
  const onTouchEnd = () => { gesture.current = null; };
  const onMouseDown = (e) => { if (scale > 1) { e.preventDefault(); gesture.current = { kind: "pan", x: e.clientX - pos.x, y: e.clientY - pos.y }; } };
  const onMouseMove = (e) => { const g = gesture.current; if (g?.kind === "pan") setPos({ x: e.clientX - g.x, y: e.clientY - g.y }); };
  const onWheel = (e) => { if (e.ctrlKey || e.metaKey) { e.preventDefault(); zoom(scale * (e.deltaY < 0 ? 1.1 : 0.9)); } };

  return (
    <div className={`space-y-2 ${className}`} data-testid="receipt-zoom-viewer">
      <div className="flex items-center gap-1 flex-wrap">
        <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => zoom(scale * 1.25)} title="Zoom in" data-testid="receipt-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
        <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => zoom(scale / 1.25)} title="Zoom out" data-testid="receipt-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
        <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => setRot((r) => (r + 270) % 360)} title="Rotate left" data-testid="receipt-rotate-left"><RotateCcw className="w-4 h-4" /></Button>
        <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => setRot((r) => (r + 90) % 360)} title="Rotate right" data-testid="receipt-rotate-right"><RotateCw className="w-4 h-4" /></Button>
        <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={reset} title="Fit to view" data-testid="receipt-zoom-reset"><Maximize2 className="w-4 h-4" /></Button>
        <span className="text-xs text-slate-500 ml-1" data-testid="receipt-zoom-level">{Math.round(scale * 100)}%{rot ? ` · ${rot}°` : ""}</span>
        <span className="text-[11px] text-slate-400 ml-auto hidden sm:inline">Pinch or Ctrl+scroll to zoom · drag to pan</span>
      </div>
      <div className="w-full h-[480px] rounded border bg-slate-50 overflow-hidden select-none" style={{ touchAction: "none", cursor: scale > 1 ? "grab" : "default" }}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onTouchEnd} onMouseLeave={onTouchEnd} onWheel={onWheel} onDoubleClick={() => zoom(scale > 1 ? 1 : 2.5)}>
        <img src={src} alt={alt} draggable={false} className="w-full h-full object-contain"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale}) rotate(${rot}deg)`, transition: gesture.current ? "none" : "transform 120ms ease-out" }} data-testid="receipt-preview-image" />
      </div>
    </div>
  );
};
